import { Box, Card, Stack, Typography, useMediaQuery } from "@mui/material"
import { CommentsList } from "./ChildrenComponents/CommentsList"
import { CommentsFilter } from "./ChildrenComponents/CommentsFilter"
import { CommentsListBox, FilterList, MainStack, MobileReplyBox } from "./style"
import { PureLightTheme } from "../../styles/theme"
import { FormBlock } from "../../common/FormGenerator"
import { getFormConfig } from "./settingsConfig"
import { useFormContext } from "react-hook-form"
import type { ProjectCommentTabCounts, TGetCommentsResponse, CommentsSortUi } from "../../api/types/projects"
import type { OpenL2ReplyThreadPayload } from "./ChildrenComponents/ReplyItem"
import { usePwaMobile } from "../../hooks/usePwaMobile"

type CommentsSectionProps = {
    onClose?: () => void
    commentsData?: TGetCommentsResponse
    onSubmitComment?: (payload: { text: string; parentId?: number }) => Promise<boolean>
    isSubmittingComment?: boolean
    isCommentsLoading?: boolean
    isCommentsError?: boolean
    loadMoreReplies?: (parentId: number) => void | Promise<void>
    loadingParentId?: number | null
    currentUserId?: number | null
    onUpdateComment?: (commentId: number, text: string) => Promise<boolean>
    isUpdatingComment?: boolean
    onDeleteComment?: (commentId: number) => Promise<boolean>
    canComment?: boolean
    onOpenReplyThread?: (p: OpenL2ReplyThreadPayload) => void
    tabCounts?: ProjectCommentTabCounts
    commentReactionCache?:
        | { kind?: 'project'; projectId: number; type: string; sort?: CommentsSortUi; threadParentId?: number }
        | { kind: 'growthTool'; growthToolId: number; type: string; sort?: CommentsSortUi; threadParentId?: number }
        | { kind: 'socialOrder'; socialOrderId: number; type: string; sort?: CommentsSortUi; threadParentId?: number }
    commentForbiddenText?: string
    highlightCommentId?: number | null
}

export const CommentsSection = ({
    onClose,
    commentsData,
    onSubmitComment,
    isSubmittingComment,
    isCommentsLoading,
    isCommentsError,
    loadMoreReplies,
    loadingParentId,
    currentUserId,
    onUpdateComment,
    isUpdatingComment,
    onDeleteComment,
    canComment = true,
    onOpenReplyThread,
    tabCounts,
    commentReactionCache,
    commentForbiddenText = "Вам запрещено комментировать в этом проекте.",
    highlightCommentId,
}: CommentsSectionProps) => {
    const isTablet = useMediaQuery(PureLightTheme.breakpoints.down("md"))
    const isPwaMobile = usePwaMobile()
    const { watch, setValue, getValues } = useFormContext()
    const mobileReplyTarget = watch('mobileReplyTarget')
    const handleCancelReply = () => {
        setValue("mobileReplyTarget", null)
        setValue("commentText", "")
    }
    const handleMainSubmit = async () => {
        if (!onSubmitComment) return
        const text = getValues("commentText")
        const parentId = mobileReplyTarget?.id
        await onSubmitComment({ text, parentId })
    }

    const commentsListSharedProps = {
        commentsData,
        onSubmitReply: onSubmitComment,
        isSubmittingComment,
        isCommentsLoading,
        isCommentsError,
        loadMoreReplies,
        loadingParentId,
        currentUserId,
        onUpdateComment,
        isUpdatingComment,
        onDeleteComment,
        onOpenReplyThread,
        commentReactionCache,
        highlightCommentId,
    }

    return (
        <>
            {!canComment && (
                <Typography variant="black-14-light" color="text.secondary" px={1.5} pb={1}>
                    {commentForbiddenText}
                </Typography>
            )}
            {isTablet ? (
                <Stack sx={MainStack}>  
                    <Stack direction="column" sx={FilterList}>
                        <Box sx={{ flexShrink: 0 }} px={1.5} pb={1.5}>
                            <CommentsFilter onClose={onClose} tabCounts={tabCounts} />
                        </Box>
                        <Box sx={CommentsListBox}>
                            <CommentsList {...commentsListSharedProps} />
                        </Box>
                    </Stack>
                    <Box p={1.5} sx={MobileReplyBox}>
                        {mobileReplyTarget && (
                            <Typography 
                                variant="cerulean-14-semiBold"
                                onClick={handleCancelReply}
                                component="div"
                                mb={0.5}
                                noWrap
                            >
                                Ответ для: {mobileReplyTarget.name}
                            </Typography>
                        )}
                        <Box sx={isPwaMobile ? { paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))' } : undefined}>
                            {canComment && (
                                <FormBlock formFieldConfig={getFormConfig('commentText', { onSend: handleMainSubmit, disabled: isSubmittingComment })} />
                            )}
                        </Box>
                    </Box>
                </Stack>
            ) : (
                <Stack sx={MainStack}>
                    <Card variant="primaryCard" sx={FilterList}>
                        <CommentsFilter tabCounts={tabCounts} />
                        {!isTablet && canComment && (
                            <FormBlock formFieldConfig={getFormConfig('commentText', { onSend: handleMainSubmit, disabled: isSubmittingComment })} />
                        )}
                        <CommentsList {...commentsListSharedProps} />
                    </Card>
                </Stack> 
            )}
        </>
    )
}
